import React, { useContext } from 'react';
import Box from '../../../../../atomicDesign/atoms/Box';
import Text from '../../../../../atomicDesign/atoms/Text';
import SimpleTable from '../../../../../atomicDesign/molecules/SimpleTable';
import DataRow from '../../../../../atomicDesign/atoms/DataRow';
import DataCell from '../../../../../atomicDesign/atoms/DataCell';
import { fmt } from './utils';
import type { UiCategorized, UiDay } from './types';
import AdminContext from '../../../../../context/admin';

type AdminContextType = {
  language?: {
    devices?: {
      detailLevel?: {
        day?: string;
      };
    };
    eosDashboard?: {
      noDataAvailable?: string;
      baselineChart?: {
        average?: string;
        total?: string;
        rightAxis?: string;
        co2Total?: string;
      };
    };
    settings?: {
      baseline?: {
        energyType?: string;
        energyTypes?: {
          [key: string]: string;
        };
      };
      areas?: {
        devices?: string;
      };
    };
  };
};

export default function DaySummary({
  dayKey,
  day,
}: {
  dayKey: string;
  day: UiDay;
}) {
  const { language } = useContext(AdminContext) as AdminContextType;

  return (
    <Box>
      <Text variant="subtitle2" sx={{ mb: 0.5 }}>
        {language?.devices?.detailLevel?.day ?? 'Day'} {day.dayNumber ?? dayKey}
      </Text>

      <SimpleTable
        columns={[
          {
            label: language?.settings?.baseline?.energyType ?? 'Energy Type',
            align: 'left',
          },
          {
            label: language?.eosDashboard?.baselineChart?.average ?? 'Avg',
            align: 'right',
          },
          {
            label: language?.eosDashboard?.baselineChart?.total ?? 'Total',
            align: 'right',
          },
          {
            label: language?.eosDashboard?.baselineChart?.rightAxis ?? 'kWp',
            align: 'right',
          },
          {
            label:
              language?.eosDashboard?.baselineChart?.co2Total ??
              'CO₂ total in kg',
            align: 'right',
          },
          {
            label: language?.settings?.areas?.devices ?? 'Devices',
            align: 'right',
          },
        ]}
      >
        {(day.categorized ?? []).map((c: UiCategorized, idx: number) => (
          <DataRow key={`${c.typeOfEnergy}-${idx}`} sx={{ cursor: 'default' }}>
            <DataCell>
              {language?.settings?.baseline?.energyTypes?.[c.typeOfEnergy] ??
                c.typeOfEnergy}
            </DataCell>
            <DataCell align="right">{fmt(Number(c.avg ?? 0))}</DataCell>
            <DataCell align="right">{fmt(Number(c.total ?? 0))}</DataCell>
            <DataCell align="right">{fmt(Number(c.kWp ?? 0))}</DataCell>
            <DataCell align="right">
              {fmt(Number(c.totalCo2 ?? 0) / 1000)}
            </DataCell>
            <DataCell align="right">{(c.breakdown ?? []).length}</DataCell>
          </DataRow>
        ))}

        {(day.categorized ?? []).length === 0 && (
          <DataRow>
            <DataCell colSpan={6}>
              <Text variant="body2">
                {language?.eosDashboard?.noDataAvailable ?? 'No data.'}
              </Text>
            </DataCell>
          </DataRow>
        )}
      </SimpleTable>
    </Box>
  );
}
